import { FavoriteButton } from '@liveauctioneers/hammer-ui-core/favoriteButton';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { EventContext } from '@/redux/modules/analytics';
import { toggleItemSave } from '@/redux/modules/item/saved/savedItems.actions';
import { isItemSaved } from '@/redux/modules/item/saved/savedItems.selectors';
import { CompleteAccountModalContext, ModalContext } from '@/redux/modules/modal';

type Props = {
    canShowEasyShipping?: boolean;
    itemId: number;
    labeled?: boolean;
};

export default function ItemCardSaveIcon({ canShowEasyShipping = false, itemId, labeled = false }: Props) {
    const saveButtonId = `saveBtn-${itemId}`;
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const location = useLocation();

    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const isFavorite = useAppSelector((state) => isItemSaved(state, itemId));

    const modalContext = useMemo<ModalContext>(
        () => ({
            eventContext: EventContext.saveItem,
            id: itemId,
            itemId,
            type: CompleteAccountModalContext.SeeSoldPrice,
        }),
        [itemId]
    );

    const onToggleSave = useCallback(() => {
        dispatch(toggleItemSave({ canHaveEasyShippingTag: canShowEasyShipping, itemId, location }));
    }, [dispatch, canShowEasyShipping, itemId, location]);

    return (
        <ShouldLogInContainer
            forceSignUp={!userLoggedIn}
            modalContext={modalContext}
            runFunction={onToggleSave}
        >
            <FavoriteButton
                aria-label="favorite button"
                className="z-1"
                favorited={isFavorite}
                id={saveButtonId}
                tooltipProps={{
                    anchorSelect: `#${saveButtonId}`,
                    content: formatMessage({ id: 'save_to_my_items' }),
                    enabled: !isFavorite && labeled,
                    place: 'top',
                    positionStrategy: 'fixed',
                }}
                variant={labeled ? 'labeled' : 'circle'}
            />
        </ShouldLogInContainer>
    );
}
